import React, { useState } from 'react';
import { useExamList } from '@/hooks/queries/exam/useList';
import { useExamCreate, useExamUpdate, useExamDelete } from '@/hooks/mutations/exam';
import { Exam, ExamCreateInput } from '@/types/lastexam';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog } from '@/components/AlertDialog';
import Loader from '@/components/Loader';
import { Plus, Edit2, Trash2, FileCheck, HelpCircle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { ExamQuestionsModal } from './components/ExamQuestionsModal';
import { Pagination } from '@/components/Pagination';

const ExamsPage = () => {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState<Exam | null>(null);

  // Questions Modal state
  const [isQuestionsModalOpen, setIsQuestionsModalOpen] = useState(false);
  const [examForQuestions, setExamForQuestions] = useState<Exam | null>(null);

  const [formData, setFormData] = useState<ExamCreateInput>({
    name: '',
    courseId: '',
    passingPercentage: 70,
    maxAttempts: 3,
    timeLimit: 60,
    shuffleQuestions: true,
    shuffleAnswers: false,
  });

  const { data: examsData, isLoading, refetch } = useExamList({
    pageNumber: page,
    pageSize: 50,
  });

  const { mutate: createExam, isPending: isCreating } = useExamCreate();
  const { mutate: updateExam, isPending: isUpdating } = useExamUpdate();
  const { mutate: deleteExam } = useExamDelete();

  const handleOpenCreate = () => {
    setSelectedExam(null);
    setFormData({
      name: '',
      courseId: '',
      passingPercentage: 70,
      maxAttempts: 3,
      timeLimit: 60,
      shuffleQuestions: true,
      shuffleAnswers: false,
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (exam: Exam) => {
    setSelectedExam(exam);
    setFormData({
      name: exam.name || '',
      courseId: exam.courseId || '',
      passingPercentage: exam.passingPercentage,
      maxAttempts: exam.maxAttempts,
      timeLimit: exam.timeLimit,
      shuffleQuestions: exam.shuffleQuestions,
      shuffleAnswers: exam.shuffleAnswers,
    });
    setIsModalOpen(true);
  };

  const handleOpenQuestions = (exam: Exam) => {
    setExamForQuestions(exam);
    setIsQuestionsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedExam) {
      updateExam(
        {
          id: selectedExam.id,
          data: {
            name: formData.name,
            passingPercentage: formData.passingPercentage,
            maxAttempts: formData.maxAttempts,
            timeLimit: formData.timeLimit,
            shuffleQuestions: formData.shuffleQuestions,
            shuffleAnswers: formData.shuffleAnswers,
          },
        },
        {
          onSuccess: () => {
            toast({ title: 'Muvaffaqiyatli', description: 'Imtihon yangilandi' });
            setIsModalOpen(false);
            refetch();
          },
          onError: () => toast({ title: 'Xatolik', description: 'Imtihon yangilanmadi', variant: 'destructive' }),
        }
      );
    } else {
      createExam(formData, {
        onSuccess: () => {
          toast({ title: 'Muvaffaqiyatli', description: 'Kurs yakuniy imtihoni yaratildi' });
          setIsModalOpen(false);
          refetch();
        },
        onError: () => toast({ title: 'Xatolik', description: 'Imtihon yaratilmadi', variant: 'destructive' }),
      });
    }
  };

  const handleDelete = () => {
    if (!selectedExam) return;
    deleteExam(selectedExam.id, {
      onSuccess: () => {
        toast({ title: 'Muvaffaqiyatli', description: "Imtihon o'chirildi" });
        setIsDeleteOpen(false);
        setSelectedExam(null);
        refetch();
      },
      onError: () => toast({ title: 'Xatolik', description: "Imtihon o'chirilmadi", variant: 'destructive' }),
    });
  };

  const examsList: Exam[] = examsData?.data?.data || examsData?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Kurs Yakuniy Imtihonlari</h1>
          <p className="text-sm text-slate-500 mt-1">Yakuniy attestatsiya va sertifikat olish imtihonlari ro'yxati</p>
        </div>
        <Button onClick={handleOpenCreate} className="gap-2 bg-primary hover:opacity-90">
          <Plus className="size-4" /> Yangi imtihon
        </Button>
      </div>

      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden p-6 space-y-6">
        {isLoading ? (
          <div className="py-12"><Loader /></div>
        ) : examsList.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <FileCheck className="size-12 mx-auto text-slate-300 mb-3" />
            <p>Hozircha kurs imtihonlari mavjud emas</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50/75 text-xs uppercase font-semibold text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3.5">Imtihon Nomi</th>
                  <th className="px-4 py-3.5">Turi</th>
                  <th className="px-4 py-3.5">O'tish Foizi</th>
                  <th className="px-4 py-3.5">Vaqt Limiti</th>
                  <th className="px-4 py-3.5">Urinishlar</th>
                  <th className="px-4 py-3.5 text-right">Amallar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {examsList.map((exam) => (
                  <tr key={exam.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 font-semibold text-slate-900">{exam.name || 'Kurs yakuniy imtihoni'}</td>
                    <td className="px-4 py-3">
                      <span className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-blue-50 text-blue-700 border border-blue-100">
                        {exam.testType || 'COURSE_FINAL'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-emerald-600">{exam.passingPercentage}%</td>
                    <td className="px-4 py-3 text-slate-600">{exam.timeLimit} daqiqa</td>
                    <td className="px-4 py-3 text-slate-600">{exam.maxAttempts} marta</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenQuestions(exam)}
                          className="h-8 px-2.5 rounded-lg text-xs gap-1 text-primary border-primary/20 hover:bg-primary/5 font-medium"
                        >
                          <HelpCircle className="size-3.5" /> Savollar
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenEdit(exam)}
                          className="size-8 text-slate-600 hover:text-primary rounded-lg"
                        >
                          <Edit2 className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setSelectedExam(exam);
                            setIsDeleteOpen(true);
                          }}
                          className="size-8 text-slate-600 hover:text-red-500 rounded-lg"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
<Pagination className="mt-4" currentPage={page} setCurrentPage={setPage} paginationInfo={examsData?.meta?.pagination ?? examsData?.data?.meta?.pagination} />
          </div>
        )}
      </div>

      {/* Create / Edit Exam Dialog */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-xl bg-white rounded-2xl p-6">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {selectedExam ? 'Imtihonni tahrirlash' : 'Yangi kurs yakuniy imtihoni'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">Imtihon Nomi</Label>
              <Input
                placeholder="Masalan: Frontend Kursi Yakuniy Attestatsiya Imtihoni"
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="rounded-xl"
                required
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">O'tish foizi (%)</Label>
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={formData.passingPercentage}
                  onChange={(e) => setFormData({ ...formData, passingPercentage: Number(e.target.value) })}
                  className="rounded-xl"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Vaqt (daqiqa)</Label>
                <Input
                  type="number"
                  min={1}
                  value={formData.timeLimit}
                  onChange={(e) => setFormData({ ...formData, timeLimit: Number(e.target.value) })}
                  className="rounded-xl"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-700">Urinishlar soni</Label>
                <Input
                  type="number"
                  min={1}
                  value={formData.maxAttempts}
                  onChange={(e) => setFormData({ ...formData, maxAttempts: Number(e.target.value) })}
                  className="rounded-xl"
                  required
                />
              </div>
            </div>

            <div className="p-4 rounded-xl bg-slate-50 border border-slate-100 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-slate-800">Savollarni aralashtirish</p>
                  <p className="text-[11px] text-slate-500">Talabaga har gal turli tartibda savollar chiqadi</p>
                </div>
                <Switch
                  checked={formData.shuffleQuestions}
                  onCheckedChange={(val) => setFormData({ ...formData, shuffleQuestions: val })}
                />
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-slate-200/60">
                <div>
                  <p className="text-xs font-semibold text-slate-800">Javoblarni aralashtirish</p>
                  <p className="text-[11px] text-slate-500">Variantlar har safar tasodifiy tartibda almashadi</p>
                </div>
                <Switch
                  checked={formData.shuffleAnswers}
                  onCheckedChange={(val) => setFormData({ ...formData, shuffleAnswers: val })}
                />
              </div>
            </div>

            <DialogFooter className="pt-4 border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl">
                Bekor qilish
              </Button>
              <Button type="submit" disabled={isCreating || isUpdating} className="rounded-xl bg-primary text-white">
                {isCreating || isUpdating ? 'Saqlanmoqda...' : 'Saqlash'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Exam Alert */}
      <AlertDialog
        isOpen={isDeleteOpen}
        setIsOpen={setIsDeleteOpen}
        alertTitle="Imtihonni o'chirishni tasdiqlaysizmi?"
        alertDescription="Ushbu imtihon va uning barcha savollari butunlay o'chiriladi."
        alertActionTitle="O'chirish"
        alertCancel="Bekor qilish"
        alertActionFunction={handleDelete}
      />

      {/* Exam Questions Modal */}
      {examForQuestions && (
        <ExamQuestionsModal
          isOpen={isQuestionsModalOpen}
          onClose={() => {
            setIsQuestionsModalOpen(false);
            setExamForQuestions(null);
          }}
          examId={examForQuestions.id}
          examName={examForQuestions.name}
        />
      )}
    </div>
  );
};

export default ExamsPage;
